import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import type { Citation } from '../../utils/wsFrames';

const SourcesRoot = styled.aside`
  margin-top: 0.65rem;
  max-width: 80%;
  min-width: 0;
  width: 100%;
  padding: 0.65rem 0.85rem;
  border-radius: 0.75rem;
  border: 1px solid ${({ theme }) => theme.colors.cardBorder};
  background: ${({ theme }) =>
    theme.darkMode ? 'rgba(255, 255, 255, 0.04)' : 'rgba(0, 0, 0, 0.03)'};
  color: ${({ theme }) => theme.colors.text};

  @media (max-width: 768px) {
    max-width: 92%;
  }
`;

const SourcesTitle = styled.h4`
  margin: 0 0 0.5rem;
  font-size: 0.8rem;
  font-weight: 700;
  letter-spacing: 0.02em;
  text-transform: uppercase;
  opacity: 0.75;
`;

const SourceList = styled.ol`
  margin: 0;
  padding-left: 1.25rem;
  display: flex;
  flex-direction: column;
  gap: 0.4rem;
`;

const SourceItem = styled.li<{ $highlight: boolean }>`
  font-size: 0.85rem;
  line-height: 1.4;
  border-radius: 0.35rem;
  padding: 0.15rem 0.25rem;
  outline: ${({ $highlight, theme }) =>
    $highlight ? `2px solid ${theme.main}` : 'none'};
  background: ${({ $highlight, theme }) =>
    $highlight
      ? theme.darkMode
        ? 'rgba(255,255,255,0.08)'
        : 'rgba(0,0,0,0.06)'
      : 'transparent'};
  transition: background 0.2s ease, outline 0.2s ease;
`;

const SourceLink = styled.a`
  color: ${({ theme }) => (theme.darkMode ? '#a0c4ff' : theme.main)};
  text-decoration: underline;
  word-break: break-word;
`;

const SourceMeta = styled.span`
  display: block;
  font-size: 0.75rem;
  opacity: 0.65;
  margin-top: 0.1rem;
`;

function domainFromUrl(url: string): string {
  try {
    return new URL(url).hostname.replace(/^www\./, '');
  } catch {
    return '';
  }
}

type SourcesListProps = {
  citations: Citation[];
  highlightIndex?: number | null;
};

const SourcesList = ({
  citations,
  highlightIndex = null,
}: SourcesListProps): JSX.Element | null => {
  const itemRefs = useRef<Record<number, HTMLLIElement | null>>({});
  const [active, setActive] = useState<number | null>(highlightIndex);

  useEffect(() => {
    setActive(highlightIndex ?? null);
    if (highlightIndex == null) return;
    const el = itemRefs.current[highlightIndex];
    el?.scrollIntoView?.({ behavior: 'smooth', block: 'nearest' });
  }, [highlightIndex]);

  if (!citations.length) return null;

  const sorted = [...citations].sort((a, b) => a.index - b.index);

  return (
    <SourcesRoot aria-label="Sources">
      <SourcesTitle>Sources</SourcesTitle>
      <SourceList>
        {sorted.map((citation) => {
          const domain = domainFromUrl(citation.url);
          return (
            <SourceItem
              key={citation.index}
              id={`citation-source-${citation.index}`}
              $highlight={active === citation.index}
              ref={(node) => {
                itemRefs.current[citation.index] = node;
              }}
            >
              {citation.url ? (
                <SourceLink
                  href={citation.url}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {citation.title || `Source ${citation.index}`}
                </SourceLink>
              ) : (
                <span>{citation.title || `Source ${citation.index}`}</span>
              )}
              {(citation.published_at || domain) && (
                <SourceMeta>
                  {[citation.published_at, domain].filter(Boolean).join(' · ')}
                </SourceMeta>
              )}
            </SourceItem>
          );
        })}
      </SourceList>
    </SourcesRoot>
  );
};

export default SourcesList;
